import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { authApi, BASE_PATH } from '../services/api';
import { initTenant } from '../services/tenant';
import { CheckCircle, XCircle, ShieldAlert } from 'lucide-react';
import { setCookie } from '../utils/cookies';
import { membershipDenial } from '@/lib/membershipErrors';
import { Button, Modal, Spinner } from '@/components/ds-zta';

// AuthCallback serves three different landings, told apart by the query string:
//
//   ?code=&state=   the shared d2auth callback (one fixed URL for the whole
//                   product). The tenant is unknown here, so the server looks it
//                   up from `state`, finishes the login and hands back a one-time
//                   claim plus the tenant's own URL to continue on.
//   ?claim=         the tenant's own origin, one redirect later. Trades the claim
//                   for the real tokens and stores them here — on the origin that
//                   will actually send them.
//   ?code=&state=   on a deployment with no AUTH_D2AUTH_REDIRECT_URI configured,
//                   the same params are the legacy per-tenant callback and are
//                   exchanged directly.
export default function AuthCallback() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [error, setError] = useState('');
  // Where "try again" should go. On the shared callback host there is no tenant in
  // the URL, so the server tells us which tenant's login page to return to.
  const [loginUrl, setLoginUrl] = useState<string | null>(null);
  // Shown as a popup when the IdP login succeeds but the workspace refuses the
  // account — not a member, suspended, inactive, wrong organisation, workspace
  // suspended, or membership could not be checked (lib/membershipErrors.ts).
  // Holds the popup title, or null.
  const [deniedTitle, setDeniedTitle] = useState<string | null>(null);
  // The OAuth authorization code (and the claim code) is single-use. React
  // StrictMode (and any effect re-run) would otherwise POST twice: the first
  // exchange succeeds and the second fails, clobbering the real error. Guard so
  // the exchange runs exactly once.
  const handledRef = useRef(false);

  useEffect(() => {
    // Denied access after a successful IdP login: show the reason, never wave
    // them through.
    const showDenied = (err: any): boolean => {
      const denial = membershipDenial(err);
      if (!denial) return false;
      setStatus('error');
      setLoginUrl(err.response?.data?.login_url || null);
      setDeniedTitle(denial.title);
      setError(denial.message);
      return true;
    };

    const storeSessionAndContinue = (data: {
      access_token: string;
      id_token: string;
      refresh_token: string;
      user_info: any;
    }) => {
      // Store tokens in cookies (7 days expiration)
      setCookie('auth_token', data.access_token, 7);
      setCookie('id_token', data.id_token, 7);
      setCookie('refresh_token', data.refresh_token, 7);
      setCookie('user_info', JSON.stringify(data.user_info), 7);

      setStatus('success');
      // A full navigation rather than a client-side one: this page can be
      // rendered outside the tenant-gated app (the shared callback landing), and
      // reloading is what re-resolves the tenant and picks up the new cookies.
      setTimeout(() => {
        window.location.replace(`${BASE_PATH}/dashboard`);
      }, 1500);
    };

    // Step 3: redeem the one-time claim on the tenant's own origin. This is the
    // only place the session becomes a cookie, which is why it is scoped
    // correctly with no cross-origin guesswork.
    const redeemClaim = async (claim: string) => {
      try {
        const { data } = await authApi.pkceClaim(claim);
        storeSessionAndContinue(data);
      } catch (err: any) {
        if (showDenied(err)) return;
        setStatus('error');
        setError(err.response?.data?.error || 'Authentication failed');
      }
    };

    // Legacy per-tenant callback: exchange the code right here. Needs the tenant
    // header, which the shared-callback landing does not set up, so resolve it
    // first.
    const exchangeOnTenantOrigin = async (code: string, state: string) => {
      try {
        await initTenant();
      } catch {
        // Non-fatal: the backend also derives the tenant from the request Host.
      }
      try {
        const { data } = await authApi.handleCallback(code, state);
        storeSessionAndContinue(data);
      } catch (err: any) {
        if (showDenied(err)) return;
        setStatus('error');
        setError(err.response?.data?.error || 'Authentication failed');
      }
    };

    // Step 2: we are on the one fixed callback URL. Hand code + state to the
    // server, which is the only party that knows which tenant this login was for.
    const completeOnSharedCallback = async (code: string, state: string) => {
      try {
        const { data } = await authApi.pkceComplete(code, state);
        // On to the tenant's own origin with the claim attached.
        window.location.replace(data.redirect_to);
      } catch (err: any) {
        // 501 = configured off; 404 = an API that predates this flow entirely
        // (a web bundle can be rolled out ahead of the API). Either way these
        // params are a legacy per-tenant callback, so exchange them directly
        // rather than failing the login.
        const unavailable =
          err.response?.status === 501 ||
          err.response?.status === 404 ||
          err.response?.data?.code === 'PKCE_DISABLED';
        if (unavailable) {
          await exchangeOnTenantOrigin(code, state);
          return;
        }
        if (showDenied(err)) return;
        setStatus('error');
        setLoginUrl(err.response?.data?.login_url || null);
        setError(err.response?.data?.error || 'Authentication failed');
      }
    };

    const handleCallback = async () => {
      if (handledRef.current) return;
      handledRef.current = true;

      const claim = searchParams.get('claim');
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const errorParam = searchParams.get('error');

      if (errorParam) {
        setStatus('error');
        setError(`Authentication failed: ${errorParam}`);
        return;
      }

      if (claim) {
        await redeemClaim(claim);
        return;
      }

      if (!code || !state) {
        setStatus('error');
        setError('Missing authorization code or state parameter');
        return;
      }

      await completeOnSharedCallback(code, state);
    };

    handleCallback();
  }, [searchParams]);

  const backToLogin = () => {
    // Always a full navigation, never a client-side one: this page is also
    // rendered outside the tenant-gated app (the shared callback landing), where
    // a route change would just re-render this same page. loginUrl is the
    // tenant's own login page when the server could work out which tenant this
    // login was for; otherwise fall back to this origin's own.
    window.location.replace(loginUrl || `${BASE_PATH}/login`);
  };

  return (
    <>
    {deniedTitle && (
      <Modal open title={deniedTitle} onClose={backToLogin}>
        <div className="flex flex-col items-center text-center sm:items-start sm:text-left">
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[var(--zta-tint-danger)]">
            <ShieldAlert className="h-6 w-6 text-[var(--danger-text)]" />
          </div>
          <p className="text-sm text-[var(--d2d-text-secondary)]">
            {error || 'Access denied. Please contact your administrator.'}
          </p>
          <Button className="mt-6" onClick={backToLogin}>
            Back to login
          </Button>
        </div>
      </Modal>
    )}
    <div className="min-h-screen flex items-center justify-center bg-[var(--d2d-bg-primary)]">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          {status === 'processing' && (
            <>
              {/* A full-page wait with no content shape to preview: a spinner,
                  not a skeleton. The heading below names the wait. */}
              <Spinner size={48} className="inline-flex justify-center text-[var(--d2d-text-primary)]" />
              <h1 className="mt-6 text-2xl font-medium text-[var(--d2d-text-primary)]">
                Processing Authentication...
              </h1>
              <p className="mt-2 text-sm text-[var(--d2d-text-secondary)]">
                Please wait while we complete your sign-in
              </p>
            </>
          )}
          
          {status === 'success' && (
            <>
              <div className="rounded-full h-12 w-12 bg-[var(--zta-tint-success)] mx-auto flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-[var(--d2d-success)]" />
              </div>
              <h1 className="mt-6 text-2xl font-medium text-[var(--d2d-text-primary)]">
                Authentication Successful!
              </h1>
              <p className="mt-2 text-sm text-[var(--d2d-text-secondary)]">
                Redirecting to dashboard...
              </p>
            </>
          )}
          
          {status === 'error' && (
            <>
              <div className="rounded-full h-12 w-12 bg-[var(--zta-tint-danger)] mx-auto flex items-center justify-center">
                <XCircle className="h-6 w-6 text-[var(--danger-text)]" />
              </div>
              <h1 className="mt-6 text-2xl font-medium text-[var(--d2d-text-primary)]">
                Authentication Failed
              </h1>
              <p className="mt-2 text-sm text-[var(--danger-text)]">
                {error}
              </p>
              <Button className="mt-4" onClick={backToLogin}>
                Try Again
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
    </>
  );
}
